import { lazy, useCallback, useEffect, useState } from 'react';
import { isNil } from 'lodash';

import { bridgeAtom, isOpenChatAtom, isTooltipClosedAtom, tooltipPagesAtom, useAtom, useValue } from '@/store';

import { AppEvents } from '@/utils';

import { useEvent, useTheme } from '@/modules/common';
import { WidgetLayout } from '@/modules/models/WidgetLayout';

const DefaultWidget = lazy(() => import('./layouts').then((module) => ({ default: module.DefaultLayout })));
const SideWidget = lazy(() => import('./layouts').then((module) => ({ default: module.Side })));

const DEFAULT_DURATION = 5000;
const DEFAULT_DELAY = 4000;

export const Widget = () => {
  const {
    widget: { widgetLayout },
  } = useTheme();

  const [isTooltipOpen, setIsTooltipOpen] = useState(false);

  const { widgetExclusions, tooltipExclusions, isEmbedded, defaultTooltipBehavior, pageType } = useValue(bridgeAtom);
  const isOpenChat = useValue(isOpenChatAtom);
  const isTooltipClosed = useValue(isTooltipClosedAtom);

  const [tooltipPages, setTooltipPages] = useAtom(tooltipPagesAtom);

  const isWidgetDisabled = Boolean(
    widgetExclusions?.some((exclusion) => new RegExp(exclusion).test(window.location.pathname))
  );
  const isTooltipDisabled = Boolean(tooltipExclusions?.includes(window.location.pathname));

  const toggleTooltip = useCallback((state?: boolean) => {
    setIsTooltipOpen((prevState) => (!isNil(state) ? state : !prevState));
  }, []);

  const onToggleTooltipEvent = useCallback(
    (event: CustomEvent<boolean>) => {
      if (isTooltipDisabled) {
        return;
      }
      toggleTooltip(event.detail ?? null);
    },
    [isTooltipDisabled, toggleTooltip]
  );

  // TODO: finish tooltip behavior
  useEffect(() => {
    if (!defaultTooltipBehavior || (isTooltipClosed && tooltipPages?.[pageType])) {
      return;
    }

    setTimeout(() => {
      setTooltipPages((prevState) => ({
        ...prevState,
        [pageType]: 1,
      }));
      setIsTooltipOpen(true);

      if (!isTooltipClosed) {
        return;
      }

      setTimeout(() => {
        setIsTooltipOpen(false);
      }, DEFAULT_DURATION);
    }, DEFAULT_DELAY);
  }, [defaultTooltipBehavior, pageType, isTooltipClosed, tooltipPages, setTooltipPages]);

  useEvent({ name: AppEvents.TOGGLE_TOOLTIP, handler: onToggleTooltipEvent });

  if (isOpenChat || isWidgetDisabled || isEmbedded) {
    return null;
  }

  switch (widgetLayout) {
    case WidgetLayout.DEFAULT:
      return <DefaultWidget isTooltipOpen={isTooltipOpen} toggleTooltip={toggleTooltip} />;

    case WidgetLayout.SIDE:
      return <SideWidget isTooltipOpen={isTooltipOpen} toggleTooltip={toggleTooltip} />;

    default:
      return <DefaultWidget isTooltipOpen={isTooltipOpen} toggleTooltip={toggleTooltip} />;
  }
};
